import { openDB } from 'idb';
import { DATA_VERSION } from './version';

const CACHE_OPEN_TIMEOUT_MS = 1500;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team', 'canned_response'];
    this.accountId = accountId;
    this.db = null;
    this.opening = null;
  }

  async initDb() {
    if (this.db) return this.db;
    if (this.opening) return this.opening;
    const dbName = `cw-store-${this.accountId}`;
    // Cache must never prevent the API client's existing network fallback.
    this.opening = new Promise((resolve, reject) => {
      let settled = false;
      const fail = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      };
      const timer = setTimeout(
        () => fail(new Error('IndexedDB cache open timed out')),
        CACHE_OPEN_TIMEOUT_MS
      );
      openDB(dbName, DATA_VERSION, {
        upgrade(db) {
          // Existing databases already carry stores from earlier versions.
          const createStore = (name, options) => {
            if (db.objectStoreNames.contains(name)) return;
            db.createObjectStore(name, options);
          };
          createStore('cache-keys');
          createStore('inbox', { keyPath: 'id' });
          createStore('label', { keyPath: 'id' });
          createStore('team', { keyPath: 'id' });
          createStore('canned_response', { keyPath: 'id' });
        },
        blocked: () => fail(new Error('IndexedDB cache upgrade blocked')),
        blocking: () => {
          this.db?.close();
          this.db = null;
        },
        terminated: () => { this.db = null; },
      }).then(db => {
        if (settled) {
          db.close();
          return;
        }
        settled = true;
        clearTimeout(timer);
        this.db = db;
        resolve(db);
      }, fail);
    });
    try {
      const db = await this.opening;
      // Store the database name in LocalStorage.
      const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
      if (!dbNames.includes(dbName)) {
        dbNames.push(dbName);
        localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
      }
      return db;
    } finally {
      this.opening = null;
    }
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);
    await this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);
    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);
    return this.db.get('cache-keys', modelName);
  }
}
